/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessage} from 'react-intl';

// Constants used in the used for product searching.
export const SINGLE_SELECTION_FILTERS = ['c_gender', 'cgid'];
export const REDUX_SORTING_PANEL_KEY = 'sorting';
export const FILTERS_ARRANGE_OPTIONS = [
	{attributeId: 'SIZE', refinementsIds:['c_shoeSize', 'c_clothingSize'], position: 2}
];
export const SHOULD_REARRANGE_REFINEMENTS = true;
export const CATEGORIES_FILTER_ID = 'c_categoryFilter';
export const DEFAULT_SEARCH_PARAMS = {limit: 24, offset: 0, sort: 'best-matches', refine: []};
export const DEFAULT_LIMIT_VALUES = [25, 50, 100]; // Page sizes
export const INFINITE_SCROLL_PAGE_LIMIT = 3;
export const FIRST_PAGE = 1;
export const MAX_PAGE_SIZE = 200;
export const PERSONALIZATION_SEARCH_REFINEMENT = 'c_availableForCustomerGroupA';
export const LOCALIZED_REFINEMENTS = [
	'category',
	'c_categoryId',
	'c_size',
	'c_hexadecBasicColor',
	'c_heelType',
	'c_heelHeight',
	'c_occasion',
	'c_categoryFilter',
	'c_gender',
	'c_materialDescription',
];
export const REFINEMENT_DISALLOW_LIST = [
	'htype',
	'c_isNew',
	'cgid',
	'iD',
	'ID',
	'online',
	'availableForCustomerGroupA',
	PERSONALIZATION_SEARCH_REFINEMENT
];
export const NEXT_DIRECTION = 'next';
export const PREV_DIRECTION = 'prev';
export const DEFAULT_DIRECTION = NEXT_DIRECTION;
export const DEFAULT_THRESHOLD = 0.9;

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5;
export const RECENT_SEARCH_KEY = 'recent-search-key';
export const RECENT_SEARCH_MIN_LENGTH = 3;
export const QUICK_SEARCH_NO_RESULT_SLUG = '/search-no-result';
export const QUICK_SEARCH_NO_QUERY_TITLE = 'quick-search-product-carousel';

// Constants for the Homepage's Shop Products section.
export const HOME_SHOP_PRODUCTS_CATEGORY_ID = 'newarrivals';
export const HOME_SHOP_PRODUCTS_LIMIT = 10;

// Constants for CMS
export const CMS_MODE_STORAGE_KEY = 'cmsMode';
export const CMS_SCHEDULED_AT_STORAGE_KEY = 'cmsScheduledAt';

// Constants for My Account page
export const MY_ACCOUNT_ORDERS_PER_PAGE = 10;
export const MY_ACCOUNT_ORDERS_PRODUCTS = 3;
export const MY_ACCOUNT_WISHLIST_PRODUCTS = {
	desktop: 5,
	mobile: 3,
};
export const MY_ACCOUNT_WISHLIST_PRODUCTS_GRID_SIZE = {
	desktop: 6,
	mobile: 2,
};

export const DEFAULT_VALID_NUMBER_OF_YEARS_IN_THE_FUTURE = 10;

// Constants for Filter's Keywords Search section
export const FILTER_SEARCH_KEY = ['c_brands', 'brand'];

// Unit delay transition
export const UNIT_DELAY_OPACITY_TRANSITION = 0.62;

// Constant for which product stock is shown in the CTA on the wishlist page
export const STOCK_LIMIT_IN_CTA = 1;

export const cssColorGroups = {
	beige: '#d3bca9',
	black: '#000000',
	blue: '#4089c0',
	brown: '#8e6950',
	green: '#88c290',
	grey: '#919191',
	gray: '#919191',
	silver: '#c0c0c0',
	navy: '#000080',
	orange: '#f4995c',
	pink: '#f5a0ca',
	purple: '#9873b9',
	red: '#df5b5b',
	white: '#FFFFFFF',
	yellow: '#fbe85a',
	gold: '#ffd700',
	miscellaneous: 'linear-gradient(to right, orange , yellow, green, cyan, blue, violet)',
};

// Color to use for the UI surrounding the page in browsers.
export const THEME_COLOR = '#0176D3';

export const FILTER_ACCORDION_SATE = 'filters-expanded-index';

export const API_ERROR_MESSAGE = defineMessage({
	id: 'global.error.something_went_wrong',
	defaultMessage: 'Something went wrong. Try again!',
});

export const HOME_HREF = '/';

export const MAX_ORDER_QUANTITY = 10;

export const urlPartPositions = {
	PATH: 'path',
	QUERY_PARAM: 'query_param',
	NONE: 'none',
};

export const DEFAULT_SITE_TITLE = 'Retail React App';

export const MAX_CACHE_AGE = 604800; // 1week
export const MAX_CACHE_AGE_MS = MAX_CACHE_AGE * 1000;

// Constant for my account sidebar
export const MY_ACCOUNT_SIDEBAR = [
	{
		key: 'myaccount',
		to: '/myaccount',
		label: 'myAccount',
		headingMob: false,
	},
	{
		key: 'orders',
		to: '/orders',
		label: 'orders',
	},
	{
		key: 'wishlist',
		to: '/wishlist',
		label: 'wishlist',
	},
	{
		key: 'personaldata',
		to: '/personaldata',
		label: 'personalData',
	},
	{
		key: 'addresses',
		to: '/addresses',
		label: 'addresses',
	},
	{
		key: 'payments',
		to: '/payments',
		label: 'payments',
	},
	{
		key: 'newsletter-preference-center',
		to: '/newsletter-preference-center',
		label: 'newsletter',
	}
];

export const ViewType = {
	ZOOM: 'zoom',
	ZOOM_FEMALE: 'zoom_female'
};

export const PRODUCT_IMAGES_SETTINGS = {
	// Zoom: the original image (pinch to zoom and the desktop zoom need it at full resolution).
	productZoom: {
		xs: {
			strip: 'false'
		}
	},
	// PDP gallery below `lg` (`ImagesCarousel`): one image as wide as the viewport.
	productCarousel: {
		xs: {
			sw: 576,
			sh: 576,
			strip: 'false'
		},
		sm: {
			sw: 768,
			sh: 768,
			strip: 'false'
		},
		md: {
			sw: 992,
			sh: 992,
			strip: 'false'
		},
	},
	productPage: {
		xs: {
			sw: 532,
			sh: 532,
		},
		sm: {
			sw: 768,
			sh: 768,
		},
		md: {
			sw: 384,
			sh: 384,
		},
		lg: {
			sw: 532,
			sh: 532,
		},
		xl: {
			sw: 465,
			sh: 465,
		},
		xxl: {
			sw: 768,
			sh: 768,
		},
	},
	productTile: {
		xs: {
			sw: 280,
			sh: 280,
			strip: 'false'
		},
		sm: {
			sw: 376,
			sh: 376,
			strip: 'false'
		},
		md: {
			sw: 241,
			sh: 241,
			strip: 'false'
		},
		lg: {
			sw: 293,
			sh: 293,
			strip: 'false'
		},
		xl: {
			sw: 343,
			sh: 343,
			strip: 'false'
		},
		xxl: {
			sw: 393,
			sh: 393,
			strip: 'false'
		},
		xxxl: {
			sw: 473,
			sh: 473,
			strip: 'false'
		},
		xxxxl: {
			sw: 500,
			sh: 500,
			strip: 'false'
		},
	},
	myAccount: {
		xs: {
			sw: 150,
			sh: 150,
		},
	},
	productLineItem: {
		xs: {
			sh: 500,
			sw: 500,
			sm: 'cut',
		},
		sm: {
			sh: 500,
			sw: 500,
			sm: 'cut',
		},
	},
	productOtherColors: {
		xs: {
			sw: 72,
			sh: 92,
		},
	},
	// `priority` (preload, high fetch priority) on the first PDP image and on the first row of the PLP.
	preloadImages: {
		pdp: true,
		plp: true
	},
	ShowPlaceholder: {
		pdp: false,
		plp: false
	},
};

export const PRODUCT_PAGINATION_THRESHOLD = 667;

export const REFINEMENTS_VALUE_SEPARATOR = '|';

export const PRODUCT_DISTINCT_SIZES = true;

export const AVAILABLE_DPIS = [1, 1.25, 1.5, 2];

//flyout without a close button in the header
export const FLYOUT_WITHOUT_CLOSE_BUTTON_IN_HEADER = ['sizeGuide', 'wishlist', 'minicart', 'login',
	'sizeSelector', 'quickSearch'];

//Time interval to change the text in the rotational banner
export const TIMEOUT_ROTATIONAL_BANNER = 4500;

// eslint-disable-next-line max-len
export const FLYOUTS_HORIZONTAL_DIRECTION = ['minicart', 'wishlist', 'myAccount', 'login', 'sizeSelector', 'countrySelector', 'languageSelector'];

export const expressPaymentButtonStyles = {
	payPal: {
		pdp: {
			xs: 55, // max allowed height for PayPal button
			lg: 37,
		},
	},
};

export const BRANDS_TABS_HEIGHT = {
	mobile: 58,
	desktop: 38
};

export const PERSONAL_SHOPPER_COOKIE = {
	cookieName: 'personalShopperUserId',
	path: '/',
	expiration: 1
};

export const UTM_SOURCE_COOKIE = {
	cookieName: 'utmSource',
	path: '/',
	expiration: 8
};

export const USER_GUID_COOKIE = {
	cookieName: 'userGuid',
	path: '/',
	expiration: 3650
};

export const GTMEVENT_BANNED_KEYS = ['managerRepository'];

export const NEWSLETTER_SOURCE = {
	SIGN_UP: 'CA_RegistrationFlyout',
	SIGN_UP_REQUIRES: 'CA_archive',
	MY_ACCOUNT: 'CA_AccountPage',
	EMARSYS_POPUP: 'CA_EmarsysPopup',
	NOTIFY_ME_POPUP: 'CA_NotifyMe',
	CHECKOUT: 'CA_Checkout',
	FOOTER: 'CA_Footer',
	NEWSLETTER_POPUP: 'CA_Footer',
	CUSTOM_POPUP: 'POPUP',
	CUSTOM_FOOTER: 'FOOTER',
	CUSTOM_MYACCOUNT: 'MYACCOUNT',
	CUSTOM_WAITINGLIST: 'WAITINGLIST',
	CUSTOM_NOTIFY_ME: 'NOTIFY_ME',
};

export const REGISTRATION_DEVICE_TYPE = {
	DESKTOP: 'desktop',
	MOBILE: 'mobile',
	APP: 'app'
};

export const CONVERSION_CHANNEL_STORAGE_KEY = 'emarsysSource';
export const CONVERSION_CHANNEL = {
	gclid: 'gclid',
	utm_medium: 'affiliate',
	utm_source: 'fb'
};

export const CURRENT_ROUTE_BANNED_URL_PARAMS = {
	'Search-Show': ['page'],
};

export const DATALAYER_CONFIGS = {
	viewItemListMaxProducts: 20,
	addProductSku: false,
	plpCategoryForProduct: false,
	showPriceValueOnly: false,
	addDimensions: false,
	variationGroupIdForCartEvents: false,
	promotionEvent: {
		trackedQueryParameters: ['HP_BAN', 'LP_BAN'],
	},
};

export const USE_SVG_PLACEHOLDERS = true;
export const PRODUCT_PLACEHOLDER_COLOR = '#F9F9F9';

export const DEFAULT_PRODUCT_HIT_COMPONENTS = [
	'Media',
	'Badges',
	'Name',
	'Description',
	'Price',
	'AvailableColors',
	'OutOfStock'
];
export const DEFAULT_CATEGORY_STATIC_REGION_LIMIT = 24;

export const PRODUCT_DESCRIPTION_MAX_LENGTH = 130;

export const CALENDAR_DATE_FORMAT = 'month/day/year';

export const DEFAULT_CONTINENTS = ['EU', 'AF', 'AS', 'OC', 'NA', 'SA'];

export const ACTIVE_DATA_ENABLED = true;

export const NEWSLETTER_SUBSCRIPTIONS = ['GT', 'F1', 'RACES', 'ESPORTS'];
export const QUICK_SEARCH_FLYOUT_INSTANCE_KEY = 'quick-search-flyout';
export const QUICK_SEARCH_PAGE_INSTANCE_KEY = 'quick-search-page';

export const SEARCH_PAGE_TYPE = 'SEARCH';

// UNUSED ON THIS BRAND, IS REQUIRED FOR CORE LOGIC ON GRID MODEL
export const GRID_CONFIG = {
	DESKTOP: {
		availableValues: [],
		default: 4
	},
	MOBILE: {
		availableValues: [],
		default: 2
	}
};

export const DATE_FORMAT_FOR_CONTINENTS = {
	'EU': 'day/month/year'
};

export const THRON_CLIENT_ID = 'ferrari';

export const SG_OPEN_REGISTER_FORM = 'OPEN_REGISTER_FORM';

export const INPUT_MAX_LENGTH = 50;
export const PHONE_MAX_LENGTH = 20;
export const CITY_MAX_LENGTH = 30;
export const ZIP_CODE_MAX_LENGTH = 10;
export const PAYMENT_CARD_MAX_LENGTH = 16;
export const MAX_PRODUCT_QUANTITY = 30;

// Constant for default birth range in the registration form on the brand level
export const DEFAULT_BIRTH_RANGE = {
	start: 18,
	end: 100
};

export const QUERY_MIN_LENGTH = 3;
export const QUICK_SEARCH_SUGGESTION_LIMIT = 3;
export const QUICK_SEARCH_FLYOUT_ID = 'quickSearch';
export const HISTORY_CONFIG = {
	store_key: 'search_history',
	max_results: 3
};

export const REFINEMENT_MAP_BY_PROVIDERS = {};

export const POPUP_IDS = {
	worldPayThreeDS: 'popupWorldPayThreeDS'
};

export const FLYOUT_CLASS_NAMES = {
	wrapper: {
		base: 'flyout__wrapper',
		afterOpen: 'flyout__wrapper--after-open',
		beforeClose: 'flyout__wrapper--before-close',
	},

	overlay: {
		base: 'flyout__overlay',
		afterOpen: 'flyout__overlay--after-open',
		beforeClose: 'flyout__overlay--before-close',
	},
};

export const VIDEO_OPTIONS_PDP = {
	autoplay: true,
	showControls: true,
	muted: true,
	loop: true,
};

export const CONTROLS_OPTIONS_PDP = {
	playButton: {
		playIcon: 'play-cv',
		pauseIcon: 'pause-cv',
		height: 25,
		width: 25,
	},
};

export const ID_PDP_ADDITIONAL_DUTIES_INFO = 'PDPAdditionalDutiesInfo';

export const NL_COOKIE_POPUP = undefined;

export const MULTISITE_AVAILABLE = undefined;
export const SSR_STYLE_ELEMENT_ID = 'SSR_style';
